import React, { useEffect, useMemo, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from "react-simple-maps";

import { geoCentroid } from "d3-geo";
import { Control, Controls, StyledMap, ZoomImage } from "./Map.styles";

import ImageZoomIn from "./icon/zoom-in.svg";
import ImageZoomOut from "./icon/zoom-out.svg";
import { animated, useSpring } from "react-spring";
import { useTranslation } from "react-i18next";

const url = "/topo_lad.json";

const center = [-3.95, 52.42];

const Map = ({ locations, activeLocation, onLocationChange }) => {
  const [position, setPosition] = useState({
    coordinates: [-3.95, 52.42],
    zoom: 1,
  });
  const [hasDragged, setDragged] = useState(false);

  const { t } = useTranslation();
  const [style, animate] = useSpring(
    { zoom: 1, coordinates: [-3.95, 52.42] },
    []
  );

  useEffect(() => {
    if (!hasDragged) {
      animate({ zoom: position.zoom, coordinates: position.coordinates });
    }
  }, [position, animate, hasDragged]);

  // useEffect(() => {

  // const locationName = useMemo(() => {
  //     const countryName = locations.find((item, index) => index === activeLocation);
  //     return countryName;
  // }, [activeLocation, locations]);

  useEffect(() => {
    if (activeLocation) {
      const [lat, lon] = activeLocation.pos;
      setPosition((pos) => ({ ...pos, zoom: 2, coordinates: [lat, lon] }));
    } else {
      setPosition((pos) => ({ ...pos, zoom: 1, coordinates: center }));
    }
  }, [activeLocation]);

  // }, [activeLocation])
  /**
   * Wen the zoom button is clicked.
   * @param {*} e
   */
  const handleZoomIn = (e) => {
    if (position.zoom >= 2) return;
    setDragged(false);
    setPosition((origPos) => ({ ...origPos, zoom: origPos.zoom * 2 }));
  };

  const handleZoomOut = (e) => {
    e.preventDefault();
    setDragged(false);
    if (position.zoom <= 1) return;
    setPosition((pos) => ({
      ...pos,
      zoom: pos.zoom / 2,
      coordinates: [-3.95, 52.42],
    }));
  };

  const handleMoveStart = (newCenter) => {
    setDragged(true);
    setPosition(newCenter);
  };

  const handleFilter = ({ constructor: { name } }) => {
    return name !== "MouseEvent";
  };

  /**
   * When the map has finished moving update our local state to match
   * @param {*} position
   * @param {*} x
   */
  const handleMoveEnd = (newPosition) => {
    if (
      JSON.stringify(position.coordinates) ===
      JSON.stringify(newPosition.coordinates)
    ) {
      setDragged(false);
    }
  };

  /**
   * When it's clicked.
   * @param {*} geography
   * @param {*} projection
   * @param {*} path
   * @param {*} locationId
   */
  const handleFocus = (geography, projection, path, locationId) => (event) => {
    setDragged(false);
    const centroid = projection.invert(path.centroid(geography));
    setPosition((pos) => ({ zoom: 1.5, coordinates: centroid }));
    onLocationChange(locationId, true);
  };

  /**
   * Keyboard events.
   * @param {*} geography
   * @param {*} projection
   * @param {*} path
   * @param {*} locationId
   */
  const handleKeyUp = (geography, projection, path, locationId) => (event) => {
    setDragged(false);

    if (event.keyCode === 27) {
      setPosition((pos) => ({ zoom: 1, coordinates: center }));
      onLocationChange(null, true);
      return;
    }
    if (event.key === "Enter") {
      setPosition((pos) => ({ zoom: 1, coordinates: center }));
      onLocationChange(locationId, true);
    }
  };

  const AnimatedZoomableGroup = animated(ZoomableGroup);

  const MAP_HEIGHT = 440;
  const MAP_WIDTH = 351;

  return (
    <StyledMap aria-labelledby="Map of Wales" tabIndex="-1">
      <ComposableMap
        projection="geoAzimuthalEqualArea"
        projectionConfig={{
          scale: 12000,
          center: center,
        }}
        width={MAP_WIDTH}
        height={MAP_HEIGHT}
        style={{ width: "100%", height: "auto" }}
      >
        <AnimatedZoomableGroup
          zoom={style.zoom}
          center={style.coordinates}
          onMoveStart={handleMoveStart}
          onMoveEnd={handleMoveEnd}
          translateExtent={[
            [0, -MAP_HEIGHT],
            [MAP_WIDTH, MAP_HEIGHT],
          ]}
        >
          <Geographies geography={url}>
            {({ geographies, projection, path }) => (
              <>
                {geographies.map((geo) => {
                  const id = locations.findIndex(
                    (item) => item.title === geo.properties.LAD13NM
                  );
                  const detail = locations[id];
                  const isSelected =
                    activeLocation?.title === geo.properties.LAD13NM;

                  let fillColor = "#E5223C";

                  if (isSelected) {
                    fillColor = "#630E34";
                  } else if (detail.house_prices.annual_change > 0) {
                    fillColor = "#007961";
                  }

                  return (
                    <Geography
                      strokeWidth="4"
                      stroke="#fff"
                      key={geo.rsmKey}
                      geography={geo}
                      onClick={handleFocus(geo, projection, path, id)}
                      onKeyUp={handleKeyUp(geo, projection, path, id)}
                      tabIndex="-1"
                      style={{
                        default: {
                          fill: fillColor,
                          outline: "none",
                        },
                        hover: {
                          fill: "#630E34",
                          outline: "none",
                        },
                        pressed: {
                          fill: "#007961",
                          outline: "none",
                        },
                      }}
                    />
                  );
                })}

                {geographies.map((geo, index) => {
                  const centroid = geoCentroid(geo);
                  const id = locations.findIndex(
                    (item) => item.title === geo.properties.LAD13NM
                  );
                  const detail = locations[id];

                  return (
                    <g key={geo.rsmKey + "-name"}>
                      <Marker
                        coordinates={centroid}
                        onClick={handleFocus(geo, projection, path, id)}
                      >
                        <g key={geo.rsmKey + "-circle"}>
                          <circle r="8" x="0" y="0" fill="#fff" />
                          <text
                            x="0"
                            y="0"
                            textAnchor="middle"
                            stroke="black"
                            fontSize="8px"
                            fontFamily="Open Sans"
                            alignmentBaseline="central"
                          >
                            {id + 1}
                          </text>
                        </g>
                      </Marker>
                    </g>
                  );
                })}
              </>
            )}
          </Geographies>
        </AnimatedZoomableGroup>
      </ComposableMap>

      <Controls>
        <Control
          tabIndex="-1"
          onClick={handleZoomIn}
          aria-label={t("map.zoomIn")}
        >
          <ZoomImage src={ImageZoomIn} alt={t("map.zoomIn")} />
        </Control>
        <Control
          tabIndex="-1"
          onClick={handleZoomOut}
          aria-label={t("map.zoomOut")}
        >
          <ZoomImage src={ImageZoomOut} alt={t("map.zoomOut")} />
        </Control>
      </Controls>
    </StyledMap>
  );
};

export default Map;
